import type { Nullable, OptionalString } from '@amzn/elevate-data-types';

import type { ElevateDateTime } from '@/common/date-time-helpers';
import type { InterviewerView } from '@/common/models';
import { EventStatus, EventType, ModelLoopEventFilterInput, QuestionShareability } from '@/graphql/types';

export const QueryKeys = {
  orgList: {
    all: ['orgList'],
    default: ['orgList', 'default'],
    simple: ['orgList', 'simple'],
  },
  org: (orgId: OptionalString) => ({
    all: [orgId],
    details: {
      all: [orgId, 'details'],
      general: [orgId, 'details', 'general'],
      roleLDAPGroups: {
        all: [orgId, 'details', 'roleLDAPGroups'],
        withFilter: (filter: OptionalString) => [orgId, 'details', 'roleLDAPGroups', filter],
      },
      defaultShepherd: [orgId, 'defaultShepherd'],
    },
    trainings: [orgId, 'trainings'],
    eventTags: [orgId, 'eventTags'],
    eventConfig: {
      all: [orgId, 'eventConfig'],
      type: (eventType: EventType) => ({
        all: [orgId, 'eventConfig', eventType],
        active: [orgId, 'eventConfig', eventType, 'active'],
      }),
      id: (configId: OptionalString) => [orgId, 'eventConfig', configId],
    },
    participant: {
      all: [orgId, 'participant'],
      type: (eventType: EventType) => ({
        all: [orgId, 'participant', eventType],
        active: [orgId, 'participant', eventType, 'active'],
      }),
      id: (participantId: OptionalString) => [orgId, 'participant', participantId],
    },
    calibration: {
      all: [orgId, 'calibration'],
      type: (eventType: EventType) => ({
        all: [orgId, 'calibration', eventType],
        active: [orgId, 'calibration', eventType, 'active'],
      }),
      id: (calibrationId: OptionalString) => [orgId, 'calibration', calibrationId],
    },
    collaborations: {
      all: [orgId, 'collaboration'],
      inbound: [orgId, 'collaboration', 'inbound'],
      outbound: [orgId, 'collaboration', 'outbound'],
    },
    interviewers: {
      all: [orgId, 'interviewers'],
      mode: (mode: InterviewerView) => ({
        all: [orgId, 'interviewers', mode],
        idOrAlias: (value: string | null | undefined) => [orgId, 'interviewers', mode, value],
      }),
    },
    ivProfile: {
      all: [orgId, 'ivProfile'],
      calibration: (id: OptionalString) => ({
        all: [orgId, 'ivProfile', 'calibration', id],
        withFilter: (filter: OptionalString) => [orgId, 'ivProfile', 'calibration', id, filter],
      }),
    },
    roleProfile: {
      all: [orgId, 'roleProfile'],
      roles: (roleNames: string[]) => ({
        include: [orgId, 'roleProfile', roleNames, 'include'],
        exclude: [orgId, 'roleProfile', roleNames, 'exclude'],
      }),
    },
    events: (eventType: Nullable<EventType>) => ({
      all: [eventType, orgId],
      withFilters: (status: Nullable<EventStatus>, attrFilters: Nullable<ModelLoopEventFilterInput | ElevateDateTime>) => [
        eventType,
        orgId,
        status,
        attrFilters,
      ],
    }),
    event: (eventType: EventType, id: OptionalString) => ({
      all: [eventType, orgId, id],
      comments: [eventType, orgId, id, 'comments'],
      history: [eventType, orgId, id, 'history'],
      recommendations: [eventType, orgId, id, 'recommendations'],
    }),
    loops: [EventType.LOOP, orgId],
    phoneScreens: [EventType.PHONE_SCREEN, orgId],
    loopTemplate: {
      all: [orgId, 'loopTemplate'],
      id: (id: OptionalString) => [orgId, 'loopTemplate', id],
    },
    phoneScreenConfig: {
      all: [orgId, 'phoneScreenConfig'],
      maxDuration: [orgId, 'phoneScreenConfig', 'maxDuration'],
    },
    profileAvailability: {
      all: [orgId, 'profileAvailability'],
    },
    barRaisers: [orgId, 'barRaiser'],
    profilesWithShepherds: {
      all: [orgId, 'profilesWithShepherds'],
    },
    calibrationShepherd: {
      all: [orgId, 'calibrationShepherd'],
    },
  }),
  feedback: {
    all: ['feedback'],
    event: (eventType: EventType, id: OptionalString) => ({
      all: ['feedback', `${eventType}-${id}`],
      profile: (profileId: OptionalString) => ['feedback', `${eventType}-${id}`, profileId],
    }),
    profile: (profileId: OptionalString) => ({
      all: ['feedback', 'profile', profileId],
      type: (eventType: EventType) => ['feedback', 'profile', profileId, eventType],
      withOrg: (orgId: OptionalString) => ['feedback', 'profile', profileId, orgId],
    }),
    id: (feedbackId: OptionalString) => ['feedback', feedbackId],
  },
  calibration: {
    all: ['calibration'],
    id: (calibrationId: OptionalString) => ['calibration', calibrationId],
    cohortSize: (id: OptionalString) => ['calibration', id, 'cohortSize'],
  },
  eventConfig: {
    all: ['eventConfig'],
    id: (eventConfigId: OptionalString) => ['eventConfig', eventConfigId],
  },
  eventParticipant: {
    all: ['eventParticipant'],
    id: (eventParticipantId: OptionalString) => ['eventParticipant', eventParticipantId],
  },
  eventConfigDefinition: {
    all: ['eventConfigDefinition'],
    id: (eventConfigDefinitionId: OptionalString) => ['eventConfigDefinition', eventConfigDefinitionId],
  },
  eventCalibration: {
    all: ['eventCalibration'],
  },
  profileAvailability: {
    all: ['profileAvailability'],
    id: (availabilityId: OptionalString) => ['profileAvailability', availabilityId],
  },
  interviewer: {
    all: ['interviewer'],
    id: (interviewerId: OptionalString) => ['interviewer', interviewerId],
  },
  eventInterview: {
    all: ['eventInterview'],
    routingAttempts: (id: OptionalString) => ['loop', id, 'routingAttempts'],
    languages: (id: OptionalString) => ['event', id, 'languages'],
  },
  loops: [EventType.LOOP],
  phoneScreens: [EventType.PHONE_SCREEN],
  peopleProfile: {
    all: ['peopleProfile'],
    user: (username: OptionalString) => ['peopleProfile', username],
  },
  learn: {
    all: ['learn'],
    user: (userId: OptionalString) => ({
      all: ['learn', userId],
      transcripts: (trainingIds: string[]) => ['learn', userId, JSON.stringify(trainingIds)],
    }),
    training: (trainingId: OptionalString) => ['learn', trainingId],
  },
  profiles: {
    barRaisers: ['profiles', 'barRaisers'],
    brits: ['profiles', 'brits'],
  },
  profile: (profileId: OptionalString) => ({
    all: [profileId],
    availability: [profileId, 'availability'],
    calibration: (calibrationId: OptionalString) => [profileId, 'calibration', calibrationId],
    calibrations: {
      all: [profileId, 'calibration'],
      withOrg: (orgId: OptionalString) => [profileId, 'calibration', 'org', orgId],
    },
    details: [profileId, 'details'],
    events: (eventType: EventType) => [eventType, profileId],
    history: [profileId, 'history'],
    preferences: [profileId, 'preferences'],
  }),
  cognito: ['cognito'],
  questionBank: {
    all: ['questionbank'],
    team: (teamID: OptionalString) => ({
      all: ['questionbank', 'teams'],
      base: ['questionbank', 'teams', teamID],
      categories: ['questionbank', 'teams', teamID, 'categories'],
      jobRoles: ['questionbank', 'teams', teamID, 'jobRoles'],
      interviewerRoles: ['questionbank', 'teams', teamID, 'interviewerRoles'],
      questions: ['questionbank', 'teams', teamID, 'questions'],
      interviewTemplates: ['questionbank', 'teams', teamID, 'interviewTemplates'],
    }),
    interviewTemplates: (interviewTemplateID: OptionalString) => ({
      all: ['questionbank', 'interviewTemplates'],
      base: ['questionbank', 'interviewTemplates', interviewTemplateID],
    }),
    questions(questionID: OptionalString) {
      return {
        base: ['questionbank', 'questions', questionID],
        shareability(shareability: QuestionShareability) {
          return ['questionbank', 'questions', 'shareability', shareability];
        },
      };
    },
    interviewTemplateQuestions(questionID: OptionalString) {
      return {
        all: ['questionbank', 'interviewTemplateQuestions'],
        base: ['questionbank', 'interviewTemplateQuestions', questionID],
      };
    },
    categoryQuestions(categoryID: OptionalString) {
      return {
        all: ['questionbank', 'categoryQuestions'],
        base: ['questionbank', 'categoryQuestions', categoryID],
      };
    },
  },
  announcements: {
    org: (orgId: OptionalString) => ({
      all: ['announcements', orgId],
      status: (status: OptionalString) => ['announcements', orgId, status],
    }),
  },
};
